import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { 
  TrendingUp, 
  CheckCircle2, 
  XCircle, 
  Building2,
  Calendar,
  User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatisticsPage = () => {
  const { t, isRTL, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-slate-500">
        {t('no_data')}
      </div>
    );
  }

  const pieData = [
    { name: t('received'), value: stats.total_received, color: '#EF4444' },
    { name: t('not_received'), value: stats.total_not_received, color: '#10B981' },
  ];

  const barData = stats.by_hospital?.map(h => ({
    name: h.hospital,
    count: h.count
  })) || [];

  return (
    <div className="space-y-8" data-testid="statistics-page">
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          {t('stats_title')}
        </h1>
        <p className="text-slate-500">
          {isRTL ? 'عرض إحصائيات استلام الدواء' : 'View medication receipt statistics'}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in stagger-1">
        <Card className="card-hover bg-gradient-to-br from-indigo-50 to-white border-indigo-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-600 mb-1">{t('total_records')}</p>
                <p className="text-4xl font-bold text-indigo-700">{stats.total_records}</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center">
                <TrendingUp className="h-7 w-7 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-red-50 to-white border-red-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 mb-1">{t('total_received')}</p>
                <p className="text-4xl font-bold text-red-700">{stats.total_received}</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 mb-1">{t('total_not_received')}</p>
                <p className="text-4xl font-bold text-emerald-700">{stats.total_not_received}</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <XCircle className="h-7 w-7 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <Card className="animate-fade-in stagger-2">
          <CardHeader>
            <CardTitle className="text-lg">
              {isRTL ? 'توزيع حالات الاستلام' : 'Receipt Status Distribution'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bar Chart */}
        <Card className="animate-fade-in stagger-3">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-sky-500" />
              {t('by_hospital')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {barData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#64748b" />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={100} 
                      stroke="#64748b"
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#0EA5E9" 
                      radius={[0, 4, 4, 0]}
                      name={isRTL ? 'عدد السجلات' : 'Records'}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  {t('no_data')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Records */}
      <Card className="animate-fade-in stagger-4">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-sky-500" />
            {t('recent_records')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_records?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_records.map((record, index) => (
                <div 
                  key={record.id || index}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors duration-150"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="h-5 w-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{record.patient_name}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span>{record.hospital_name}</span>
                        <span>•</span>
                        <span>{record.receipt_date}</span>
                      </div>
                    </div>
                  </div>
                  <Badge 
                    className={record.receipt_status === 'received' 
                      ? 'bg-red-500 hover:bg-red-600' 
                      : 'bg-emerald-500 hover:bg-emerald-600'
                    }
                  >
                    {record.receipt_status === 'received' ? t('received') : t('not_received')}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              {t('no_data')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StatisticsPage;
